import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faBriefcase, faFilePdf, faScroll, faPaperPlane, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { IMAGENES, ORIGEN, VARIEDADES_CAFE, URL_REGULACIONES } from '../data/cafes';
import { PARTS_CSS, C, Nav, Hero, Origin, Catalog, Segmentos, Quality, Footer } from './proposalParts';

const COTIZADOR_PIN = process.env.REACT_APP_COTIZADOR_PIN || 'XAVANA2025';

// Estados de la República Mexicana (para el formulario de compra).
const ESTADOS_MX = [
  'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas',
  'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima', 'Durango', 'Estado de México',
  'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'Michoacán', 'Morelos', 'Nayarit',
  'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí',
  'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas',
];

const parsePrecio = (p) => Number(String(p).replace(/[^0-9.]/g, '')) || 0;

const getDescuentoVolumen = (kg) => {
  if (kg >= 100) return 0.12;
  if (kg >= 50) return 0.08;
  if (kg >= 20) return 0.05;
  return 0;
};

const cotLabel = { display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#444', fontSize: '13px' };
const cotInput = {
  width: '100%', padding: '11px', borderRadius: '8px',
  border: `1px solid ${C.bronce}`, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
};

export default function HomeDesktop() {
  // ── Formulario de compra público (sin PIN, sin precios) ──
  const [mostrarCompra, setMostrarCompra] = useState(false);
  const [enviandoCompra, setEnviandoCompra] = useState(false);
  const [compra, setCompra] = useState({
    empresa: '', contacto: '', telefono: '', email: '', estado: '', consumo: 20, cafes: [],
  });

  // ── Cotizador por agente (protegido con PIN, con precios) ──
  const [mostrarAgente, setMostrarAgente] = useState(false);
  const [pinOk, setPinOk] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [generando, setGenerando] = useState(false);
  const [cot, setCot] = useState({ restaurante: '', contacto: '', telefono: '', email: '', consumo: 20, cafes: [] });

  const abrirCompra = () => setMostrarCompra(true);
  const cerrarCompra = () => setMostrarCompra(false);

  const abrirAgente = () => { setPinError(''); setMostrarAgente(true); };
  const cerrarAgente = () => { setMostrarAgente(false); setPinInput(''); setPinError(''); };

  const validarPin = (e) => {
    e.preventDefault();
    if (pinInput.trim() === COTIZADOR_PIN) { setPinOk(true); setPinError(''); }
    else setPinError('PIN incorrecto. Solicítalo al área comercial.');
  };

  const toggleCafeCompra = (id) =>
    setCompra((prev) => ({
      ...prev,
      cafes: prev.cafes.includes(id) ? prev.cafes.filter((c) => c !== id) : [...prev.cafes, id],
    }));

  const toggleCafeCot = (id) =>
    setCot((prev) => ({
      ...prev,
      cafes: prev.cafes.includes(id) ? prev.cafes.filter((c) => c !== id) : [...prev.cafes, id],
    }));

  const lineasCotizacion = useMemo(() => {
    const kg = Number(cot.consumo) || 0;
    const descuento = getDescuentoVolumen(kg);
    return cot.cafes.map((id) => {
      const cafe = VARIEDADES_CAFE.find((c) => c.id === id);
      const lista = parsePrecio(cafe.precio);
      const final = lista * (1 - descuento);
      return { nombre: cafe.nombre, tipo: cafe.tipo, lista, descuento, final, mensual: final * kg };
    });
  }, [cot.cafes, cot.consumo]);

  // ── Solicitud de compra (PDF SIN precios; es una solicitud, no una cotización) ──
  const generarSolicitudPDF = async () => {
    if (!compra.empresa.trim() || !compra.contacto.trim())
      return alert('Captura al menos el nombre de la empresa y de la persona de contacto.');
    if (!compra.estado)
      return alert('Selecciona el estado de la República.');
    if (!compra.telefono.trim() && !compra.email.trim())
      return alert('Captura un teléfono o un correo de contacto.');
    if (compra.cafes.length === 0) return alert('Selecciona al menos un café de interés.');

    setEnviandoCompra(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');
      const doc = new jsPDF();
      const verde = [46, 91, 46];
      const dorado = [193, 154, 107];
      const kg = Number(compra.consumo) || 0;
      const hoy = new Date();
      const folio = `SOL-${hoy.getFullYear()}${String(hoy.getMonth() + 1).padStart(2, '0')}${String(hoy.getDate()).padStart(2, '0')}-${String(Math.floor(Math.random() * 9000) + 1000)}`;
      const cafesNombres = compra.cafes.map((id) => VARIEDADES_CAFE.find((c) => c.id === id)?.nombre).filter(Boolean);

      doc.setFillColor(...verde);
      doc.rect(0, 0, 210, 32, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.text('XAVANA CAFÉ', 14, 15);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text('Solicitud de compra de café', 14, 23);
      doc.setFontSize(9);
      doc.text(`Folio: ${folio}`, 196, 12, { align: 'right' });
      doc.text(`Fecha: ${hoy.toLocaleDateString('es-MX')}`, 196, 18, { align: 'right' });

      doc.setTextColor(51, 51, 51);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Datos de la empresa', 14, 45);
      doc.setDrawColor(...dorado);
      doc.line(14, 47, 196, 47);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Empresa: ${compra.empresa}`, 14, 55);
      doc.text(`Contacto: ${compra.contacto}`, 14, 61);
      if (compra.telefono) doc.text(`Teléfono: ${compra.telefono}`, 14, 67);
      if (compra.email) doc.text(`Correo: ${compra.email}`, 110, 67);
      doc.text(`Estado: ${compra.estado}`, 14, 73);
      doc.text(`Consumo estimado: ${kg} kg/mes`, 110, 73);

      autoTable(doc, {
        startY: 82,
        head: [['Cafés de interés', 'Tipo']],
        body: compra.cafes.map((id) => {
          const cafe = VARIEDADES_CAFE.find((c) => c.id === id);
          return [cafe?.nombre || '', cafe?.tipo || ''];
        }),
        headStyles: { fillColor: verde, textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [244, 247, 244] },
        styles: { fontSize: 10, cellPadding: 4 },
        margin: { left: 14, right: 14 },
      });

      const finalY = (doc.lastAutoTable?.finalY || 90) + 12;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(90, 90, 90);
      doc.text(
        'Esta solicitud no incluye precios. Nuestro equipo comercial te contactará con una propuesta a la medida.',
        14, finalY, { maxWidth: 182 }
      );
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...verde);
      doc.text('Santiago de Querétaro, Qro.  |  Xicotepec de Juárez, Pue.', 14, finalY + 14);

      doc.save(`Solicitud_Xavana_${compra.empresa.replace(/[^a-z0-9]/gi, '_')}.pdf`);
      alert(`¡Gracias! Tu solicitud (${folio}) se descargó como PDF y quedó lista para enviar a nuestro equipo comercial. Cafés de interés: ${cafesNombres.join(', ')}.`);
      cerrarCompra();
    } catch (err) {
      console.error('Error generando la solicitud', err);
      alert('Ocurrió un error al generar el PDF.');
    } finally {
      setEnviandoCompra(false);
    }
  };

  const generarCotizacionPDF = async () => {
    if (!cot.restaurante.trim() || !cot.contacto.trim())
      return alert('Captura al menos el nombre del restaurante y de la persona de contacto.');
    if (!cot.telefono.trim() && !cot.email.trim())
      return alert('Captura un teléfono o un correo de contacto.');
    if (cot.cafes.length === 0) return alert('Selecciona al menos un café de interés.');

    setGenerando(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');
      const doc = new jsPDF();
      const verde = [46, 91, 46];
      const dorado = [193, 154, 107];
      const kg = Number(cot.consumo) || 0;
      const descuento = getDescuentoVolumen(kg);
      const hoy = new Date();
      const vigencia = new Date();
      vigencia.setDate(vigencia.getDate() + 15);
      const folio = `XAV-${hoy.getFullYear()}${String(hoy.getMonth() + 1).padStart(2, '0')}${String(hoy.getDate()).padStart(2, '0')}-${String(Math.floor(Math.random() * 9000) + 1000)}`;

      doc.setFillColor(...verde);
      doc.rect(0, 0, 210, 32, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.text('XAVANA CAFÉ', 14, 15);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text('Propuesta comercial de abasto de café', 14, 23);
      doc.setFontSize(9);
      doc.text(`Folio: ${folio}`, 196, 12, { align: 'right' });
      doc.text(`Fecha: ${hoy.toLocaleDateString('es-MX')}`, 196, 18, { align: 'right' });
      doc.text(`Vigencia: ${vigencia.toLocaleDateString('es-MX')}`, 196, 24, { align: 'right' });

      doc.setTextColor(51, 51, 51);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Cliente', 14, 45);
      doc.setDrawColor(...dorado);
      doc.line(14, 47, 196, 47);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Establecimiento: ${cot.restaurante}`, 14, 55);
      doc.text(`Contacto: ${cot.contacto}`, 14, 61);
      if (cot.telefono) doc.text(`Teléfono: ${cot.telefono}`, 14, 67);
      if (cot.email) doc.text(`Correo: ${cot.email}`, 110, 67);
      doc.text(`Consumo promedio estimado: ${kg} kg/mes`, 14, 73);
      doc.text(`Descuento por volumen aplicado: ${(descuento * 100).toFixed(0)}%`, 110, 73);

      autoTable(doc, {
        startY: 82,
        head: [['Café', 'Tipo', 'Precio lista/kg', 'Desc.', 'Precio Xavana/kg', 'Costo mensual est.']],
        body: lineasCotizacion.map((l) => [
          l.nombre, l.tipo,
          `$${l.lista.toFixed(2)}`,
          `${(l.descuento * 100).toFixed(0)}%`,
          `$${l.final.toFixed(2)}`,
          `$${l.mensual.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        ]),
        headStyles: { fillColor: verde, textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [244, 247, 244] },
        styles: { fontSize: 9, cellPadding: 3 },
        margin: { left: 14, right: 14 },
      });

      const finalY = (doc.lastAutoTable?.finalY || 90) + 12;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(90, 90, 90);
      doc.text(
        'Precios en MXN por kilogramo. Incluye logística exprés (entregas < 24 h) bajo política "Cero Agotados".',
        14, finalY, { maxWidth: 182 }
      );
      doc.text('Propuesta sujeta a disponibilidad y vigencia indicada. Gracias por su preferencia.', 14, finalY + 6, { maxWidth: 182 });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...verde);
      doc.text('Santiago de Querétaro, Qro.  |  Xicotepec de Juárez, Pue.', 14, finalY + 18);

      doc.save(`Cotizacion_Xavana_${cot.restaurante.replace(/[^a-z0-9]/gi, '_')}.pdf`);
    } catch (err) {
      console.error('Error generando cotización', err);
      alert('Ocurrió un error al generar el PDF.');
    } finally {
      setGenerando(false);
    }
  };

  // Escape + scroll-lock mientras algún modal está abierto
  const algunModal = mostrarCompra || mostrarAgente;
  useEffect(() => {
    if (!algunModal) return;
    const onKey = (e) => { if (e.key === 'Escape') { cerrarCompra(); cerrarAgente(); } };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [algunModal]);

  return (
    <div className="xv-root" style={{ background: C.crema, color: C.tinta, fontFamily: "'Lato', sans-serif", overflowX: 'hidden' }}>
      <style>{PARTS_CSS}</style>
      <Nav />
      <Hero
        img={IMAGENES.sierraNeblina}
        eyebrow={ORIGEN.region}
        titulo={ORIGEN.titulo}
        subtitulo={ORIGEN.heroSubtitulo}
        primary={{ href: '#origen', label: 'Conoce nuestro origen' }}
        secondary={{ href: '#catalogo', label: 'Ver catálogo' }}
      />
      <Origin imageRight />
      <Catalog />
      <Segmentos />
      <Quality />
      <Footer onAgente={abrirAgente} />

      {/* Botón flotante principal: formulario de compra (público, sin PIN) */}
      <button
        onClick={abrirCompra}
        aria-label="Abrir formulario de compra"
        style={{
          position: 'fixed', bottom: '28px', right: '28px', zIndex: 900,
          background: C.verde, color: '#fff', border: 'none', borderRadius: '50px',
          padding: '14px 24px', fontWeight: 700, fontSize: '14px', cursor: 'pointer',
          boxShadow: '0 8px 24px rgba(46,91,46,.40)', display: 'flex', alignItems: 'center', gap: '8px',
          transition: 'transform .2s ease, box-shadow .2s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 30px rgba(46,91,46,.5)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 8px 24px rgba(46,91,46,.40)'; }}
      >
        <FontAwesomeIcon icon={faPaperPlane} /> Solicitar cotización
      </button>

      {/* ── MODAL FORMULARIO DE COMPRA (público) ── */}
      {mostrarCompra && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,.78)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 10000, backdropFilter: 'blur(6px)', padding: '20px',
          }}
          onClick={cerrarCompra}
        >
          <div
            role="dialog" aria-modal="true" aria-label="Formulario de compra"
            tabIndex={-1}
            style={{
              background: '#fff', padding: '35px', borderRadius: '20px',
              width: '100%', maxWidth: '760px', maxHeight: '92vh',
              overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,.4)', position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={cerrarCompra} aria-label="Cerrar"
              style={{ position: 'absolute', top: '18px', right: '20px', background: 'transparent', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#aaa' }}>
              ×
            </button>

            <h3 style={{ color: C.verde, fontFamily: "'Playfair Display', Georgia, serif", fontSize: '1.7rem', borderBottom: `2px solid ${C.bronce}`, paddingBottom: '12px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FontAwesomeIcon icon={faBuilding} style={{ fontSize: '1.3rem' }} /> Solicitud de compra
            </h3>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
              Cuéntanos sobre tu empresa y tu consumo. Nuestro equipo comercial te contactará con una propuesta a la medida.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '18px' }}>
              <div>
                <label style={cotLabel}>Empresa *</label>
                <input style={cotInput} value={compra.empresa} onChange={(e) => setCompra({ ...compra, empresa: e.target.value })} placeholder="Ej. Grupo Restaurantero MX" />
              </div>
              <div>
                <label style={cotLabel}>Persona de contacto *</label>
                <input style={cotInput} value={compra.contacto} onChange={(e) => setCompra({ ...compra, contacto: e.target.value })} placeholder="Ej. Juan Pérez" />
              </div>
              <div>
                <label style={cotLabel}>Teléfono</label>
                <input style={cotInput} value={compra.telefono} onChange={(e) => setCompra({ ...compra, telefono: e.target.value })} placeholder="55 1234 5678" />
              </div>
              <div>
                <label style={cotLabel}>Correo electrónico</label>
                <input style={cotInput} type="email" value={compra.email} onChange={(e) => setCompra({ ...compra, email: e.target.value })} placeholder="contacto@empresa.com" />
              </div>
              <div>
                <label style={cotLabel}>Estado de la República *</label>
                <select
                  style={{ ...cotInput, background: '#fff', cursor: 'pointer' }}
                  value={compra.estado}
                  onChange={(e) => setCompra({ ...compra, estado: e.target.value })}
                >
                  <option value="">Selecciona un estado…</option>
                  {ESTADOS_MX.map((estado) => (
                    <option key={estado} value={estado}>{estado}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '18px' }}>
              <label style={cotLabel}>
                Consumo estimado (kg/mes): <strong style={{ color: C.verde }}>{compra.consumo} kg</strong>
              </label>
              <input type="range" min="1" max="200" value={compra.consumo}
                onChange={(e) => setCompra({ ...compra, consumo: Number(e.target.value) })}
                style={{ width: '100%', accentColor: C.verde }} />
            </div>

            <label style={cotLabel}>Cafés de interés *</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px', marginBottom: '22px' }}>
              {VARIEDADES_CAFE.map((cafe) => {
                const activo = compra.cafes.includes(cafe.id);
                return (
                  <div key={cafe.id}
                    role="checkbox" aria-checked={activo} tabIndex={0}
                    aria-label={`${cafe.nombre} — ${activo ? 'seleccionado' : 'no seleccionado'}`}
                    onClick={() => toggleCafeCompra(cafe.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCafeCompra(cafe.id); } }}
                    style={{ border: `2px solid ${activo ? C.verde : '#e0e0e0'}`, background: activo ? '#f4f7f4' : '#fff', borderRadius: '10px', padding: '10px 12px', cursor: 'pointer', transition: 'all .2s' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 'bold', fontSize: '14px', color: '#333' }}>{cafe.nombre}</span>
                      <span style={{ color: activo ? C.verde : '#bbb', fontWeight: 'bold' }}>{activo ? '✓' : '+'}</span>
                    </div>
                    <small style={{ color: C.bronce, fontWeight: 'bold' }}>{cafe.tipo}</small>
                  </div>
                );
              })}
            </div>

            <button disabled={enviandoCompra} onClick={generarSolicitudPDF} style={{
              width: '100%', padding: '15px', background: enviandoCompra ? '#888' : C.verde,
              color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 'bold', fontSize: '16px',
              cursor: enviandoCompra ? 'default' : 'pointer',
            }}>
              {enviandoCompra ? 'Enviando…' : <><FontAwesomeIcon icon={faPaperPlane} /> Enviar solicitud</>}
            </button>
          </div>
        </div>
      )}

      {/* ── MODAL COTIZADOR POR AGENTE (PIN) ── */}
      {mostrarAgente && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,.78)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 10000, backdropFilter: 'blur(6px)', padding: '20px',
          }}
          onClick={cerrarAgente}
        >
          <div
            role="dialog" aria-modal="true" aria-label="Cotizador para el equipo comercial"
            tabIndex={-1}
            style={{
              background: '#fff', padding: '35px', borderRadius: '20px',
              width: '100%', maxWidth: pinOk ? '760px' : '440px', maxHeight: '92vh',
              overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,.4)', position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={cerrarAgente} aria-label="Cerrar"
              style={{ position: 'absolute', top: '18px', right: '20px', background: 'transparent', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#aaa' }}>
              ×
            </button>

            {!pinOk ? (
              <div style={{ textAlign: 'center', padding: '10px 5px' }}>
                <div style={{ fontSize: '40px', marginBottom: '14px', color: C.verde }}><FontAwesomeIcon icon={faLock} /></div>
                <h3 style={{ color: C.verde, fontFamily: "'Playfair Display', Georgia, serif", fontSize: '1.6rem', marginBottom: '8px' }}>
                  Cotización por agente
                </h3>
                <p style={{ color: '#666', fontSize: '14px', marginBottom: '22px' }}>
                  Esta herramienta es exclusiva para vendedores Xavana. Ingresa tu PIN para generar cotizaciones con precios.
                </p>
                <form onSubmit={validarPin}>
                  <input
                    type="password" value={pinInput} onChange={(e) => setPinInput(e.target.value)}
                    placeholder="PIN de vendedor" autoFocus
                    style={{ width: '100%', padding: '14px', borderRadius: '10px', border: `2px solid ${C.bronce}`, fontSize: '18px', textAlign: 'center', letterSpacing: '4px', marginBottom: '14px', outline: 'none' }}
                  />
                  {pinError && <div style={{ color: '#8B0001', fontSize: '13px', marginBottom: '12px' }}>{pinError}</div>}
                  <button type="submit" style={{ width: '100%', padding: '14px', background: C.verde, color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 'bold', fontSize: '16px', cursor: 'pointer' }}>
                    Desbloquear cotizador
                  </button>
                </form>
              </div>
            ) : (
              <div>
                <h3 style={{ color: C.verde, fontFamily: "'Playfair Display', Georgia, serif", fontSize: '1.7rem', borderBottom: `2px solid ${C.bronce}`, paddingBottom: '12px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FontAwesomeIcon icon={faBriefcase} style={{ fontSize: '1.3rem' }} /> Nueva cotización
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '18px' }}>
                  <div>
                    <label style={cotLabel}>Nombre del restaurante *</label>
                    <input style={cotInput} value={cot.restaurante} onChange={(e) => setCot({ ...cot, restaurante: e.target.value })} placeholder="Ej. Café del Centro" />
                  </div>
                  <div>
                    <label style={cotLabel}>Persona de contacto *</label>
                    <input style={cotInput} value={cot.contacto} onChange={(e) => setCot({ ...cot, contacto: e.target.value })} placeholder="Ej. Juan Pérez" />
                  </div>
                  <div>
                    <label style={cotLabel}>Teléfono</label>
                    <input style={cotInput} value={cot.telefono} onChange={(e) => setCot({ ...cot, telefono: e.target.value })} placeholder="55 1234 5678" />
                  </div>
                  <div>
                    <label style={cotLabel}>Correo electrónico</label>
                    <input style={cotInput} type="email" value={cot.email} onChange={(e) => setCot({ ...cot, email: e.target.value })} placeholder="contacto@negocio.com" />
                  </div>
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <label style={cotLabel}>
                    Consumo promedio estimado (kg/mes): <strong style={{ color: C.verde }}>{cot.consumo} kg</strong>
                  </label>
                  <input type="range" min="1" max="200" value={cot.consumo}
                    onChange={(e) => setCot({ ...cot, consumo: Number(e.target.value) })}
                    style={{ width: '100%', accentColor: C.verde }} />
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    Descuento por volumen: <strong>{(getDescuentoVolumen(Number(cot.consumo)) * 100).toFixed(0)}%</strong>
                  </div>
                </div>

                <label style={cotLabel}>Cafés de interés *</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px', marginBottom: '20px' }}>
                  {VARIEDADES_CAFE.map((cafe) => {
                    const activo = cot.cafes.includes(cafe.id);
                    return (
                      <div key={cafe.id}
                        role="checkbox" aria-checked={activo} tabIndex={0}
                        aria-label={`${cafe.nombre} — ${activo ? 'seleccionado' : 'no seleccionado'}`}
                        onClick={() => toggleCafeCot(cafe.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCafeCot(cafe.id); } }}
                        style={{ border: `2px solid ${activo ? C.verde : '#e0e0e0'}`, background: activo ? '#f4f7f4' : '#fff', borderRadius: '10px', padding: '10px 12px', cursor: 'pointer', transition: 'all .2s' }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 'bold', fontSize: '14px', color: '#333' }}>{cafe.nombre}</span>
                          <span style={{ color: activo ? C.verde : '#bbb', fontWeight: 'bold' }}>{activo ? '✓' : '+'}</span>
                        </div>
                        <small style={{ color: C.bronce, fontWeight: 'bold' }}>{cafe.precio}/kg · {cafe.tipo}</small>
                      </div>
                    );
                  })}
                </div>

                {lineasCotizacion.length > 0 && (
                  <div style={{ background: '#f9f9f5', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px', fontSize: '13.5px' }}>
                    {lineasCotizacion.map((l) => (
                      <div key={l.nombre} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                        <span>{l.nombre}</span>
                        <span style={{ color: C.verde, fontWeight: 'bold' }}>
                          ${l.final.toFixed(2)}/kg → ${l.mensual.toLocaleString('es-MX', { maximumFractionDigits: 0 })}/mes
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <a href={URL_REGULACIONES} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '14px', fontSize: '12px', color: C.bronce, fontWeight: 'bold', textDecoration: 'underline' }}>
                  <FontAwesomeIcon icon={faScroll} /> Regulaciones Oficiales MX
                </a>

                <button disabled={generando} onClick={generarCotizacionPDF} style={{
                  width: '100%', padding: '15px', background: generando ? '#888' : C.verde,
                  color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 'bold', fontSize: '16px',
                  cursor: generando ? 'default' : 'pointer',
                }}>
                  {generando ? 'Generando…' : <><FontAwesomeIcon icon={faFilePdf} /> Generar propuesta en PDF</>}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
